import { useCallback, useEffect, useMemo } from 'react';
import { RootState, useAppDispatch, useAppSelector } from '../../../../../stores/redux.store';
import {
  useAnalyticsClient,
  useFormSubmittedEvent,
} from '../../../../../utilities/analytics/analytics-web-client';
import {
  verifyEmailOtp,
  verifyEmailOtpFailure,
} from '../../../../../actions/verify-email-otp-actions';
import { VerifyEmailOtpError } from '../../../../../types';
import { IntlShape, useIntl } from 'react-intl';
import { useRegisterComponent } from '../../../../orchestrator';
import { OTP_CODE_FULL_LENGTH } from '../../../../../components/VerifyEmailOtp/constants';
import { VERIFY_EMAIL_OTP_PAGE_ID } from '../constants';
import { useNavigateSoftOrHard } from '../../../../../utilities/hooks/useNavigateSoftOrHard';

type OtpErrorInfo = {
  errorCode: VerifyEmailOtpError;
  errorMessage: string;
};

type VerifyEmailOtpHookProps = {
  onSubmit?: () => void;
  onFailure?: () => void;
  onSuccess?: () => void;
  /**
   * @deprecated
   *
   * When submission is complete, the user will be redirected away from IDAC, via
   * a series of backend redirects, to their product destination with a valid session.
   *
   * We no longer support opting out of this behaviour, to preserve reliability and
   * integrity of user flows & session distribution.
   */
  autoRedirect?: boolean;
};

const verifyEmailOtpSelectorFn = (state: RootState) => state.verifyEmailOtp;

export const useVerifyEmailOtp = ({
  onSubmit,
  onFailure,
  onSuccess,
  autoRedirect = true,
}: VerifyEmailOtpHookProps = {}) => {
  useRegisterComponent(useVerifyEmailOtp.name);
  const { formatMessage } = useIntl();

  const {
    redirectType,
    error: errorCode,
    isLoading,
    redirectTo,
  } = useAppSelector(verifyEmailOtpSelectorFn);

  const dispatch = useAppDispatch();
  const analyticsClient = useAnalyticsClient();
  const navigate = useNavigateSoftOrHard();

  useEffect(() => {
    if (redirectTo && autoRedirect) {
      navigate(redirectTo);
    }
  }, [autoRedirect, redirectTo, navigate]);

  const trackSubmitEmailOtp = useFormSubmittedEvent(VERIFY_EMAIL_OTP_PAGE_ID, 'verifyEmailOtp', {
    signupType: redirectType,
    source: 'emailOtpScreen',
  });

  const trackSubmissionFailure = useFormSubmittedEvent(VERIFY_EMAIL_OTP_PAGE_ID, 'verifyEmailOtp', {
    signupType: redirectType,
    result: 'invalid_otp_length',
  });

  const onVerifyEmailOtp = useCallback(
    (otp: string) => {
      onSubmit?.();
      if (otp.length !== OTP_CODE_FULL_LENGTH) {
        const error = 'invalid_otp_length';
        onFailure?.();
        trackSubmissionFailure();
        dispatch(verifyEmailOtpFailure(error));
      } else {
        onSuccess?.();
        trackSubmitEmailOtp();
        dispatch(verifyEmailOtp(otp, VERIFY_EMAIL_OTP_PAGE_ID, analyticsClient));
      }
    },
    [
      analyticsClient,
      dispatch,
      onFailure,
      onSubmit,
      onSuccess,
      trackSubmissionFailure,
      trackSubmitEmailOtp,
    ]
  );

  const error: OtpErrorInfo | undefined = useMemo(
    () =>
      errorCode
        ? {
            errorCode,
            errorMessage: otpErrorMessage(errorCode, formatMessage),
          }
        : undefined,
    [errorCode, formatMessage]
  );

  return {
    onVerifyEmailOtp,
    onInvalidEmailOtpLength: trackSubmissionFailure,
    redirectTo,
    error,
    isLoading,
  };
};

const otpErrorMessage = (
  errorCode: VerifyEmailOtpError,
  formatMessage: IntlShape['formatMessage']
) => {
  switch (errorCode) {
    case 'invalid_otp_length':
      return formatMessage({
        id: 'validate.email.otp.error.otp_length',
        defaultMessage: 'Complete the verification code',
      });
    case 'invalid_otp':
      return formatMessage({
        id: 'validate.email.otp.error.invalid_otp',
        defaultMessage: 'Provided verification code is invalid',
      });
    case 'invalid_token':
      return formatMessage({
        id: 'validate.email.otp.error.invalid_token',
        defaultMessage: 'Invalid token',
      });
    case 'expired_token':
      return formatMessage({
        id: 'validate.email.otp.error.expired_token',
        defaultMessage: 'Token expired',
      });
    case 'internal_server_error':
      return formatMessage({
        id: 'validate.email.otp.error.internal_server_error',
        defaultMessage: 'Internal server error',
      });
    case 'rate_limit_exceeded':
    case 'Too many requests. Please inspect Retry-After header.':
      return formatMessage({
        id: 'validate.email.otp.error.rate_limit_exceeded',
        defaultMessage: 'Rate limit exceeded. Try again in a couple of minutes.',
      });
    case 'unexpected_error':
    default:
      return formatMessage({
        id: 'validate.email.otp.error.unexpected_error',
        defaultMessage: 'Unexpected error',
      });
  }
};
